/**
 * Maps the v2 asset API wire shapes (`dataStudioAssets.types.ts`) onto the
 * contracts the Data Studio UI renders (`dataStudioTables.types.ts`).
 *
 * This is the whole boundary between the two layers. The spec's snake_case,
 * positional rows, raw profiling values, and absent semantic type all stop
 * here — which is why a spec change costs a mapper edit rather than a grid
 * rewrite.
 *
 * Replaces the previous mappers for the single `/profile` endpoint we had
 * designed speculatively. That endpoint does not exist: the real API splits
 * the same screen across the asset, columns, and rows endpoints.
 */
import { resolveConnectorBranding } from "../connectors/connectorRegistry.mapper";
import {
  classifyColumnType,
  isLowCardinality,
} from "./dataStudioColumnTypeClassifier";
import type {
  AssetSearchListResponse,
  AssetSearchResult,
  ColumnDetail,
  ColumnListResponse,
  ColumnProfilingSummary,
  ConnectorColumn,
  RowsResponse,
  TabularAsset,
} from "./dataStudioAssets.types";
import type {
  DataStudioColumnDetailFact,
  GetDataStudioColumnDetailsResponse,
} from "./dataStudioColumnDetails.types";
import type {
  DataStudioColumnProfilingSummary,
  DataStudioColumnQuality,
  DataStudioProfileClassificationCounts,
  DataStudioProfileColumn,
  DataStudioProfileRow,
  DataStudioTableSummary,
  GetDataStudioTableColumnsResponse,
  GetDataStudioTableProfileResponse,
  GetDataStudioTableRowsResponse,
  ListDataStudioTablesResponse,
} from "./dataStudioTables.types";

// ---------------------------------------------------------------------------
// Quality
// ---------------------------------------------------------------------------

/**
 * Four-tier banding confirmed with product: excellent (90-100, production-
 * ready), good (70-89, usable with minor gaps), fair (50-69, meaningful gaps —
 * review before use), poor (0-49, significant issues — do not use for
 * reporting). The API sends a raw 0-100 score and no banding, so the
 * thresholds stay frontend-owned.
 */
export const deriveQualityStatus = (
  score: number,
): DataStudioColumnQuality["status"] =>
  score >= 90 ? "excellent" : score >= 70 ? "good" : score >= 50 ? "fair" : "poor";

/** `undefined` for an unscored column, so the grid omits the quality strip. */
export const mapQualityScore = (
  score: number | null | undefined,
): DataStudioColumnQuality | undefined =>
  typeof score === "number"
    ? { score, status: deriveQualityStatus(score) }
    : undefined;

// ---------------------------------------------------------------------------
// Profiling summary
// ---------------------------------------------------------------------------

/**
 * Metrics shown first, in this order, when the backend sends them. Everything
 * else follows alphabetically.
 *
 * The API declares `profiling_summary` as free-form
 * (`additionalProperties: true`) and gives no ordering, so display order has to
 * come from somewhere — an unstable `Object.keys` order would reshuffle the
 * statistics block between renders. Both `min`/`max` and `min_value`/`max_value`
 * appear in the spec's own examples, so both spellings are listed
 * (`DATA_STUDIO_API_ALIGNMENT_PLAN.md` §7 Q2).
 */
export const PROFILING_SUMMARY_KEY_ORDER: readonly string[] = [
  "total_count",
  "distinct_count",
  "null_count",
  "duplicate_count",
  "min",
  "min_value",
  "max",
  "max_value",
  "mean",
  "median",
  "stddev",
  "profiling_sample_size",
] as const;

const LABEL_OVERRIDES: Record<string, string> = {
  stddev: "Std deviation",
  min_value: "Min",
  max_value: "Max",
  profiling_sample_size: "Sample size",
};

/** `null_count` → `Null count`; keeps acronyms the backend already capitalised. */
export const humanizeProfilingKey = (key: string): string => {
  const override = LABEL_OVERRIDES[key];
  if (override) return override;
  const words = key.replace(/[_-]+/g, " ").trim();
  if (!words) return key;
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/**
 * Renders a raw profiling value for display. Integers get thousands
 * separators, fractions are capped at two decimals, and non-scalars fall back
 * to JSON so an unexpected nested value is visible rather than `[object
 * Object]`.
 */
export const formatProfilingValue = (value: unknown): string => {
  if (value === null || value === undefined) return "—";
  if (typeof value === "number") {
    if (!Number.isFinite(value)) return String(value);
    return Number.isInteger(value)
      ? value.toLocaleString()
      : Number(value.toFixed(2)).toLocaleString();
  }
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "string") return value;
  return JSON.stringify(value);
};

const orderProfilingKeys = (keys: string[]): string[] => {
  const preferred = PROFILING_SUMMARY_KEY_ORDER.filter((key) =>
    keys.includes(key),
  );
  const rest = keys
    .filter((key) => !PROFILING_SUMMARY_KEY_ORDER.includes(key))
    .sort((left, right) => left.localeCompare(right));
  return [...preferred, ...rest];
};

/**
 * Turns the API's raw `profiling_summary` into the label→value pairs the
 * statistics block renders, in a stable order.
 */
export const formatProfilingSummary = (
  summary: ColumnProfilingSummary | null | undefined,
): DataStudioColumnProfilingSummary => {
  if (!summary) return {};
  return orderProfilingKeys(Object.keys(summary)).reduce<
    DataStudioColumnProfilingSummary
  >((accumulator, key) => {
    accumulator[humanizeProfilingKey(key)] = formatProfilingValue(summary[key]);
    return accumulator;
  }, {});
};

/** Reads a numeric metric under either spelling the spec uses. */
const readNumericMetric = (
  summary: ColumnProfilingSummary | null | undefined,
  ...keys: string[]
): number | null => {
  if (!summary) return null;
  for (const key of keys) {
    const value = summary[key];
    if (typeof value === "number" && Number.isFinite(value)) return value;
  }
  return null;
};

// ---------------------------------------------------------------------------
// Columns
// ---------------------------------------------------------------------------

const reportUnknownDataType = (dataType: string): void => {
  if (process.env.NODE_ENV === "development") {
    // eslint-disable-next-line no-console
    console.warn(
      `[dataStudio] unclassified column data_type "${dataType}" — defaulted to string`,
    );
  }
};

/**
 * Maps one API column onto the grid's column contract.
 *
 * `kind`/`format` are derived rather than read: the API has no semantic type
 * field. The cardinality hint comes from the same `profiling_summary` the
 * statistics block uses, so a low-cardinality text column can still render as
 * a category — see `dataStudioColumnTypeClassifier.ts`.
 */
export const mapConnectorColumn = (
  column: ConnectorColumn,
): DataStudioProfileColumn => {
  const cardinality = {
    distinctCount: readNumericMetric(column.profiling_summary, "distinct_count"),
    totalCount: readNumericMetric(
      column.profiling_summary,
      "total_count",
      "profiling_sample_size",
    ),
  };
  const { kind, format } = classifyColumnType(column, {
    cardinality,
    onUnknownDataType: reportUnknownDataType,
  });

  return {
    id: column.id,
    name: column.name,
    kind,
    format,
    keyKind: column.is_primary_key ? "PK" : column.is_foreign_key ? "FK" : undefined,
    isImportant: false,
    ordinalPosition: column.ordinal_position,
    dataType: column.data_type,
    isNullable: column.is_nullable,
    quality: mapQualityScore(column.data_quality_score),
    profilingSummary: formatProfilingSummary(column.profiling_summary),
    visualSpec: column.profiling_preview_spec ?? null,
    availableActions: { canTransform: true, canOpenMenu: true },
  };
};

/**
 * Maps a page of columns, ordered by the source's own `ordinal_position` so the
 * grid matches the table as it exists upstream. The API returns columns in
 * `ordinal_position` order already; sorting makes the grid independent of that
 * guarantee.
 */
export const mapConnectorColumns = (
  columns: ConnectorColumn[],
): DataStudioProfileColumn[] =>
  [...columns]
    .sort((left, right) => left.ordinal_position - right.ordinal_position)
    .map(mapConnectorColumn);

/**
 * Maps one page of the columns endpoint onto the profile grid's horizontal
 * infinite-scroll contract — the column-dimension counterpart of
 * `mapRowsResponse`. `OffsetPaginationMeta` carries no previous offset of its
 * own, so `hasPrevious`/`previousOffset` are derived the same way the rows
 * mapper derives them: present only once `offset` has advanced past the start
 * of the collection.
 */
export const mapColumnsPageResponse = (
  response: ColumnListResponse,
): GetDataStudioTableColumnsResponse => {
  const { meta } = response;
  const hasPrevious = meta.offset > 0;

  return {
    columns: mapConnectorColumns(response.data),
    columnPageInfo: {
      total: meta.total_count,
      nextOffset: meta.has_next ? meta.offset + meta.limit : null,
      hasMore: meta.has_next,
      previousOffset: hasPrevious ? Math.max(0, meta.offset - meta.limit) : null,
      hasPrevious,
    },
  };
};

/** Derived from the mapped columns' `kind` — the API has no classification counts. */
export const countClassifications = (
  columns: DataStudioProfileColumn[],
): DataStudioProfileClassificationCounts => ({
  dimensions: columns.filter(
    (column) =>
      column.kind === "dimension" ||
      column.kind === "string" ||
      column.kind === "geo",
  ).length,
  measures: columns.filter(
    (column) => column.kind === "integer" || column.kind === "numeric",
  ).length,
  ids: columns.filter((column) => column.kind === "id").length,
});

// ---------------------------------------------------------------------------
// Asset header
// ---------------------------------------------------------------------------

/**
 * Maps the asset endpoint's response plus its already-mapped columns onto the
 * profile contract the header and grid read.
 *
 * `attributes` and every field inside it are optional in the spec, so
 * `rowCount`/`columnCount`/`quality` may all be absent — `columnCount` falls
 * back to the number of columns actually received, which is the better number
 * to show anyway when the asset omitted it.
 *
 * `pipeline` stays empty: pipeline mutations have no endpoints on this spec
 * revision, so the strip renders its no-history state.
 */
export const mapTabularAssetProfile = (
  asset: TabularAsset,
  columns: DataStudioProfileColumn[],
): GetDataStudioTableProfileResponse => {
  const branding = resolveConnectorBranding(asset.connector.type);

  return {
    table: {
      id: asset.id,
      name: asset.name,
      source: {
        id: asset.connector.id,
        name: branding.displayName,
        logoUrl: branding.iconUrl,
      },
      rowCount: asset.attributes?.row_count,
      columnCount: asset.attributes?.column_count ?? columns.length,
      classificationCounts: countClassifications(columns),
      quality: mapQualityScore(asset.attributes?.data_quality_score),
    },
    columns,
    pipeline: {
      stages: [],
      activeStageId: null,
      canUndo: false,
      canRedo: false,
    },
  };
};

// ---------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------

/**
 * Zips the rows endpoint's positional tuples into the keyed rows the grid
 * reads (`row.values[column.id]`).
 *
 * The API sends `data` as arrays aligned to `columns`, with a genuinely absent
 * cell as `null` rather than omitted — so index `i` of every tuple always
 * belongs to `columns[i]` and alignment cannot shift. Booleans are stringified
 * here because the grid's cell contract carries `string | number | null`.
 *
 * Row ids are positional (`row-<absolute index>`): the API exposes no row
 * primary key, and offset-based identity keeps keys stable across pages as
 * long as the offset is included.
 */
export const mapRowsResponse = (
  response: RowsResponse,
): GetDataStudioTableRowsResponse => {
  const { rows: rowMeta } = response.meta;
  const columnOrder = response.columns.map((column) => ({
    id: column.id,
    name: column.name,
    dataType: column.data_type,
  }));

  const rows: DataStudioProfileRow[] = response.data.map((tuple, rowIndex) => {
    const values: DataStudioProfileRow["values"] = {};
    response.columns.forEach((column, columnIndex) => {
      const cell = tuple[columnIndex];
      values[column.id] =
        typeof cell === "boolean" ? String(cell) : cell ?? null;
    });
    return { id: `row-${rowMeta.offset + rowIndex}`, values };
  });

  const hasPrevious = rowMeta.offset > 0;

  return {
    rows,
    rowPageInfo: {
      total: rowMeta.total_count,
      nextOffset: rowMeta.has_next ? rowMeta.offset + rowMeta.limit : null,
      hasMore: rowMeta.has_next,
      previousOffset: hasPrevious
        ? Math.max(0, rowMeta.offset - rowMeta.limit)
        : null,
      hasPrevious,
    },
    columnOrder,
  };
};

// ---------------------------------------------------------------------------
// Asset search
// ---------------------------------------------------------------------------

/**
 * Maps one search hit onto a table summary. The connector logo and display
 * name come from the shared catalog lookup — the API sends only
 * `connector.type`, and `resolveConnectorBranding` is the same resolver the
 * connectors screens use, so branding stays identical across features.
 */
export const mapAssetSearchResult = (
  result: AssetSearchResult,
): DataStudioTableSummary => {
  const branding = resolveConnectorBranding(result.connector.type);

  return {
    id: result.id,
    name: result.name,
    parentName: result.parent_name ?? null,
    label: result.parent_name ? `${result.parent_name} · ${result.name}` : result.name,
    qualifiedName: result.qualified_name,
    assetType: result.asset_type,
    discoveredAt: result.discovered_at,
    source: {
      id: result.connector.id,
      key: result.connector.type,
      name: branding.displayName,
      logoUrl: branding.iconUrl,
    },
  };
};

export const mapAssetSearchResponse = (
  response: AssetSearchListResponse,
): ListDataStudioTablesResponse => ({
  items: response.data.map(mapAssetSearchResult),
  total: response.meta.total_count,
  limit: response.meta.limit,
  offset: response.meta.offset,
  hasNext: response.meta.has_next,
});

// ---------------------------------------------------------------------------
// Column detail
// ---------------------------------------------------------------------------

const SEMANTIC_ROLE_LABELS: Record<string, string> = {
  id: "Identifier",
  dimension: "Dimension",
  string: "Attribute",
  integer: "Measure",
  numeric: "Measure",
  datetime: "Date/time",
  geo: "Geography",
  boolean: "Flag",
};

/**
 * Human label for a column's role. Derived from the classification rather than
 * read from the API, which sends no semantic type — a primary key reads as
 * "Primary key" rather than the generic "Identifier" because the flag is
 * more specific than the inferred kind.
 */
export const deriveSemanticRole = (column: ConnectorColumn): string => {
  if (column.is_primary_key) return "Primary key";
  if (column.is_foreign_key) return "Foreign key";
  const { kind } = classifyColumnType(column);
  return SEMANTIC_ROLE_LABELS[kind] ?? "Attribute";
};

/**
 * Builds the modal's fact rows from the column's own flags plus its profiling
 * metrics.
 *
 * The previous contract had the backend send this list ready-made. It does
 * not: the API sends `data_type`, the key/nullability flags,
 * `ordinal_position`, `data_quality_score`, and a free-form
 * `profiling_summary`. The source-derived facts come first because they are
 * always present, then whatever profiling the backend computed.
 */
export const buildColumnDetailFacts = (
  column: ColumnDetail,
): DataStudioColumnDetailFact[] => {
  const facts: DataStudioColumnDetailFact[] = [
    { key: "Data type", value: column.data_type },
    { key: "Semantic role", value: deriveSemanticRole(column) },
    { key: "Nullable", value: column.is_nullable ? "Yes" : "No" },
    { key: "Position", value: String(column.ordinal_position) },
  ];

  if (typeof column.data_quality_score === "number") {
    facts.push({ key: "Quality score", value: `${column.data_quality_score}/100` });
  }

  Object.entries(formatProfilingSummary(column.profiling_summary)).forEach(
    ([key, value]) => facts.push({ key, value }),
  );

  return facts;
};

/**
 * Maps the column-detail endpoint onto the modal's contract. The nullable
 * curated fields become empty string/array so the form's inputs stay
 * controlled — `null` would make React treat them as uncontrolled.
 */
export const mapColumnDetail = (
  column: ColumnDetail,
): GetDataStudioColumnDetailsResponse => ({
  column: {
    id: column.id,
    name: column.name,
    dataType: column.data_type,
    semanticRole: deriveSemanticRole(column),
  },
  facts: buildColumnDetailFacts(column),
  description: column.description ?? "",
  notes: column.notes ?? [],
  synonyms: column.synonyms ?? [],
});

/** Re-exported so call sites can reuse the classifier's cardinality rule. */
export { isLowCardinality };
